import { Component, inject, effect, untracked } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { KeyView, IKeyView } from "../../models/view/key/key.view";
import { jsonValue } from "@bitwarden/angular/validators/json.validator";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "../../models/enum/confirmation.enum";

function isValidKeyViewArray(items: any): items is KeyView[] {
  return Array.isArray(items) && (items as IKeyView[]).every((i) => i.apiKey);
}

const EMPTY_VALUE = "";

@Component({
  selector: "app-keychain",
  templateUrl: "./keychain.component.html",
})
export class KeychainComponent {
  private userStoreService = inject(UserStoreService);
  private confirmationDialogService = inject(ConfirmationDialogService);
  protected formControl = new FormControl(EMPTY_VALUE, {
    validators: [Validators.required, jsonValue()],
  });
  private previousValue = this.formControl.value;
  protected isOpen = false;
  protected isSaving = false;
  protected isTogglingOpen = false;

  async toggleOpen() {
    if (this.isSaving || this.isTogglingOpen) {
      return;
    }

    this.isTogglingOpen = true;
    try {
      if (this.isOpen) {
        if (this.formControl.dirty) {
          const confirmed = await this.confirmationDialogService.confirmFor(
            ConfirmationEnum.discardingKeychainChanges,
          );
          if (!confirmed) {
            return;
          }
        }
        this.updateLocalState(this.serializeCollection(this.userStoreService.key.collection()));
      }
      this.isOpen = !this.isOpen;
    } finally {
      this.isTogglingOpen = false;
    }
  }

  constructor() {
    effect(() => {
      const json = this.serializeCollection(this.userStoreService.key.collection());

      untracked(() => {
        if (this.formControl.dirty) {
          this.previousValue = json;
          return;
        }
        if (json === this.formControl.value) {
          return;
        }
        this.updateLocalState(json);
      });
    });
  }

  save() {
    if (this.formControl.invalid) {
      return;
    }

    const value = this.formControl.value;
    const parsed = JSON.parse(value);

    if (!isValidKeyViewArray(parsed)) {
      this.updateLocalState(this.previousValue);
      return;
    }

    this.isSaving = true;
    this.userStoreService.key
      .saveKeys(parsed)
      .then((res) => {
        if (!res) {
          this.updateLocalState(this.previousValue);
          return;
        }
        this.updateLocalState(this.serializeCollection(this.userStoreService.key.collection()));
      })
      .catch(() => {
        this.updateLocalState(this.previousValue);
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  private updateLocalState(value: string) {
    this.formControl.setValue(value, { emitEvent: false });
    this.formControl.markAsPristine();
    this.previousValue = value;
  }

  private serializeCollection(collection: KeyView[] | null): string {
    return collection ? JSON.stringify(collection, null, 2) : EMPTY_VALUE;
  }
}
